import React, { useState, useEffect, useMemo } from 'react';
import { formatRupiah, formatRupiahFull } from '../data/financialData';
import { useData } from '../DataContext';

interface AnalyticToolsPageProps {
  activeEntity?: string;
  startIdx: number;
  endIdx: number;
  globalArea: string;
  globalBranch: string;
}

type TabId = 'pivot' | 'description' | 'ratios';

export const AnalyticToolsPage: React.FC<AnalyticToolsPageProps> = ({
  activeEntity,
  globalArea,
  globalBranch,
}) => {
  const { MONTHS, branches, branchToArea } = useData();

  // GL Data state (lazy loaded)
  const [glData, setGlData] = useState<{ columns: string[]; data: any[][] } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Tab State
  const [activeTab, setActiveTab] = useState<TabId>('pivot');

  // Filter States
  const [selectedArea, setSelectedArea] = useState<string>(globalArea !== 'ALL' ? globalArea : '');
  const [selectedBranch, setSelectedBranch] = useState<string>(globalBranch !== 'ALL' ? globalBranch : '');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  
  // Tab 1: Pivot Dimensions
  const [primaryDimension, setPrimaryDimension] = useState<'area' | 'branch' | 'account' | 'month'>('area');
  const [secondaryDimension, setSecondaryDimension] = useState<'area' | 'branch' | 'account' | 'month'>('account');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Tab 3: Description semantic search & tag selector
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [customTagInput, setCustomTagInput] = useState<string>('');

  // Selected drilldown transactions state
  const [drilldownTitle, setDrilldownTitle] = useState<string>('');
  const [drilldownRows, setDrilldownRows] = useState<any[][]>([]);

  // Entity Accent Colors & UI Theme Skin
  const accentColor = activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#10b981';
  const accentGlow = activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.15)' : 'rgba(16, 185, 129, 0.15)';
  const accentLight = activeEntity === 'JUARA_GADAI' ? 'var(--amber-light)' : 'var(--emerald-light)';

  // Sync parent layout filters
  useEffect(() => {
    if (globalBranch !== 'ALL') {
      setSelectedBranch(globalBranch);
      const parentArea = branchToArea[globalBranch] || '';
      setSelectedArea(parentArea);
    } else if (globalArea !== 'ALL') {
      setSelectedArea(globalArea);
      setSelectedBranch('');
    } else {
      setSelectedArea('');
      setSelectedBranch('');
    }
  }, [globalArea, globalBranch, branchToArea]);

  // Load GL Data
  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    setError(null);

    const loadGL = async () => {
      try {
        const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
        const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

        const qParams = new URLSearchParams({
          entity: activeEntity ?? 'JUARA_GADAI'
        });

        if (selectedBranch) {
          qParams.set('branch', selectedBranch);
        } else if (selectedArea) {
          qParams.set('area', selectedArea);
        }

        if (selectedMonth) {
          qParams.set('mo', selectedMonth);
        }

        const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${API_KEY}` }
        });
        if (!res.ok) {
          throw new Error('Gagal mengunduh Buku Besar (GL) dari database cloud.');
        }
        const data = await res.json();
        if (mounted) {
          setGlData(data);
          setIsLoading(false);
        }
      } catch (err: any) {
        console.error(err);
        if (mounted) {
          setError(err.message || String(err));
          setIsLoading(false);
        }
      }
    };

    loadGL();
    return () => { mounted = false; };
  }, [activeEntity, selectedArea, selectedBranch, selectedMonth]);

  // Reset drilldown when filters change
  useEffect(() => {
    setDrilldownRows([]);
    setDrilldownTitle('');
  }, [selectedArea, selectedBranch, selectedMonth, activeTab]);

  // Group branches by area for layout options
  const branchesByArea = useMemo(() => {
    const groups: Record<string, typeof branches> = {
      Depok: [],
      Bogor: [],
      Bekasi: [],
    };
    branches.forEach((b) => {
      if (groups[b.area]) {
        groups[b.area].push(b);
      }
    });
    return groups;
  }, [branches]);

  // Fast optimized filtering of GL data (already filtered by API, just return it)
  const filteredGLRows = useMemo(() => {
    if (!glData) return [];
    return glData.data;
  }, [glData]);

  // Toggle dynamic pivot groups
  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Helper to map record dimension value to readable text
  const getDimValue = (row: any[], dim: 'area' | 'branch' | 'account' | 'month'): string => {
    const [mo, acc_code, acc_name, , branchCode, areaName] = row;
    switch (dim) {
      case 'area':
        return areaName || 'UNKNOWN AREA';
      case 'branch':
        return branchCode || 'UNKNOWN BRANCH';
      case 'account':
        return `${acc_code} - ${acc_name}`;
      case 'month':
        return mo || 'UNKNOWN MONTH';
      default:
        return '';
    }
  };

  // Tab 1: Pivot Calculation Logic
  const pivotData = useMemo(() => {
    if (activeTab !== 'pivot' || filteredGLRows.length === 0) return [];

    const rollup: Record<string, {
      key: string;
      label: string;
      actual: number;
      count: number;
      subgroups: Record<string, {
        key: string;
        label: string;
        actual: number;
        count: number;
        rows: any[][];
      }>;
    }> = {};

    filteredGLRows.forEach((row) => {
      const val1 = getDimValue(row, primaryDimension);
      const val2 = getDimValue(row, secondaryDimension);
      const amount = row[8] || 0;

      if (!rollup[val1]) {
        rollup[val1] = {
          key: val1,
          label: val1,
          actual: 0,
          count: 0,
          subgroups: {},
        };
      }

      rollup[val1].actual += amount;
      rollup[val1].count += 1;

      if (!rollup[val1].subgroups[val2]) {
        rollup[val1].subgroups[val2] = {
          key: val2,
          label: val2,
          actual: 0,
          count: 0,
          rows: [],
        };
      }

      rollup[val1].subgroups[val2].actual += amount;
      rollup[val1].subgroups[val2].count += 1;
      rollup[val1].subgroups[val2].rows.push(row);
    });

    // Convert rollup object to sorted array
    return Object.values(rollup)
      .map((group) => {
        const sortedSubgroups = Object.values(group.subgroups).sort((a, b) => Math.abs(b.actual) - Math.abs(a.actual));
        return {
          ...group,
          subgroups: sortedSubgroups,
        };
      })
      .sort((a, b) => Math.abs(b.actual) - Math.abs(a.actual));
  }, [filteredGLRows, primaryDimension, secondaryDimension, activeTab]);

  // Tab 3: Semantic Tag Cloud builder
  const semanticTags = useMemo(() => {
    return [
      'Gaji', 'Bonus', 'THR', 'Sewa', 'Listrik', 'Air', 'Telepon', 'Internet', 'ATK', 
      'Marketing', 'Iklan', 'Promosi', 'Transport', 'Bensin', 'Tol', 'Perjalanan', 
      'Konsumsi', 'Makan', 'Meeting', 'Pajak', 'BPJS', 'Bunga', 'Lelang', 'Repair'
    ];
  }, []);

  // Description explorer filter logic (Enriched with Multi-word AND search)
  const tagAnalysis = useMemo(() => {
    if (activeTab !== 'description' || filteredGLRows.length === 0) {
      return { tagTrend: [], totalTagAmount: 0, tagPercentage: 0 };
    }

    const rawTagToSearch = (selectedTag || customTagInput || '').trim().toLowerCase();
    if (!rawTagToSearch) return { tagTrend: [], totalTagAmount: 0, tagPercentage: 0 };

    const searchKeywords = rawTagToSearch.split(/\s+/).filter(k => k.length > 0);

    let totalTagAmount = 0;
    let totalTotalAmount = 0;

    const monthlyDistribution: Record<string, number> = {};
    MONTHS.forEach(m => { monthlyDistribution[m.month] = 0; });

    const matchedTrans: any[][] = [];

    filteredGLRows.forEach((row) => {
      const [mo, , , desc, , , , , amount] = row;
      const isExpense = String(row[1]).startsWith('12') || String(row[1]).startsWith('11');
      if (!isExpense) return; // Only analyze expenses

      totalTotalAmount += amount;
      
      const textToScan = String(desc).toLowerCase();
      const isMatch = searchKeywords.every(kw => textToScan.includes(kw));

      if (isMatch) {
        totalTagAmount += amount;
        monthlyDistribution[mo] = (monthlyDistribution[mo] || 0) + amount;
        matchedTrans.push(row);
      }
    });

    const tagTrend = Object.entries(monthlyDistribution).map(([mo, val]) => {
      const mLabel = MONTHS.find(m => m.month === mo)?.label || mo;
      return { month: mo, label: mLabel, amount: val };
    });

    const tagPercentage = totalTotalAmount !== 0 ? (totalTagAmount / totalTotalAmount) * 100 : 0;

    return {
      tagTrend,
      totalTagAmount,
      tagPercentage,
      matchedTrans,
    };
  }, [filteredGLRows, selectedTag, customTagInput, MONTHS, activeTab]);

  // Tab 7: Financial Ratios & Health Audit
  const ratiosData = useMemo(() => {
    if (activeTab !== 'ratios' || filteredGLRows.length === 0) return null;

    let revenue = 0;
    let opex = 0;
    let personnel = 0;
    let officeOps = 0;
    let cogs = 0;

    filteredGLRows.forEach((row) => {
      const [ , acc_code, , , , , , , amount] = row;
      const code = String(acc_code);

      if (code.startsWith('10')) {
        revenue += (parseFloat(row[7]) || 0) - (parseFloat(row[6]) || 0);
      } else if (code.startsWith('11')) {
        cogs += amount;
      } else if (code.startsWith('12')) {
        opex += amount;
        if (code.startsWith('121') || code.startsWith('122') || code.startsWith('123') || code.startsWith('124') || code.startsWith('125')) {
          personnel += amount;
        } else if (code.startsWith('126')) {
          officeOps += amount;
        }
      }
    });

    const opexRatio = revenue !== 0 ? (opex / revenue) * 100 : 0;
    const personnelToOpex = opex !== 0 ? (personnel / opex) * 100 : 0;
    const opsToOpex = opex !== 0 ? (officeOps / opex) * 100 : 0;
    const efficiencyIndex = opex !== 0 ? revenue / opex : 0;

    // Build Health rating
    let score = 100;
    const factors: Array<{ label: string; score: number; status: 'good' | 'warning' | 'critical' }> = [];

    // Factor 1: Opex to Revenue (Target < 65%)
    if (opexRatio > 85) {
      score -= 35;
      factors.push({ label: 'Rasio Efisiensi Opex tinggi (>85% Pendapatan)', score: -35, status: 'critical' });
    } else if (opexRatio > 65) {
      score -= 15;
      factors.push({ label: 'Rasio Efisiensi Opex moderat (65% - 85% Pendapatan)', score: -15, status: 'warning' });
    } else {
      factors.push({ label: 'Rasio Efisiensi Opex prima (<65% Pendapatan)', score: 0, status: 'good' });
    }

    // Factor 2: Personnel cost to Opex (Target < 50%)
    if (personnelToOpex > 65) {
      score -= 15;
      factors.push({ label: 'Biaya Karyawan sangat mendominasi Opex (>65%)', score: -15, status: 'warning' });
    } else {
      factors.push({ label: 'Proporsi Biaya Karyawan seimbang (<65% Opex)', score: 0, status: 'good' });
    }

    // Factor 3: Operations & Office Costs
    if (opsToOpex > 35) {
      score -= 10;
      factors.push({ label: 'Biaya Kantor & Logistik tinggi (>35% Opex)', score: -10, status: 'warning' });
    } else {
      factors.push({ label: 'Biaya Kantor & Logistik terkontrol (<35% Opex)', score: 0, status: 'good' });
    }

    const finalScore = Math.max(10, score);
    const healthRating = finalScore >= 80 ? 'EXCELLENT' : finalScore >= 60 ? 'HEALTHY' : finalScore >= 40 ? 'NEEDS ATTENTION' : 'CRITICAL LIMIT';

    return {
      opexRatio,
      personnelToOpex,
      opsToOpex,
      efficiencyIndex,
      healthScore: finalScore,
      healthRating,
      factors,
      revenue,
      totalOpex: opex,
      personnel,
      officeOps,
    };
  }, [filteredGLRows, activeTab]);

  // Handler to open drill-down detail window
  const openDrilldown = (title: string, rows: any[][]) => {
    setDrilldownTitle(title);
    setDrilldownRows(rows);
    setTimeout(() => {
      const el = document.getElementById('drilldown-explorer');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="analytics-container animate-in" style={{ padding: '24px', color: 'var(--text-primary)' }}>
      {/* Visual styling overrides */}
      <style dangerouslySetInnerHTML={{__html: `
        .analytics-tab-btn {
          padding: 10px 20px;
          border: none;
          background: transparent;
          cursor: pointer;
          color: var(--text-muted);
          border-bottom: 2px solid transparent;
          font-weight: 500;
          font-size: 13px;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .analytics-tab-btn.active {
          color: var(--text-primary);
          border-bottom: 2px solid ${accentColor};
          font-weight: 700;
        }
        .analytics-tab-btn:hover:not(.active) {
          color: var(--text-primary);
          background: rgba(255,255,255,0.02);
        }
        .kpi-metric-card {
          background: var(--bg-card);
          border: 1px solid var(--border-light);
          padding: 20px;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-md);
          display: flex;
          flex-direction: column;
          gap: 6px;
          position: relative;
          overflow: hidden;
        }
        .kpi-metric-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 3px;
          background: ${accentColor};
        }
      `}} />

      {/* Header and Controls */}
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>🔬</span> Analytic Tools
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Platform analisis keuangan dinamis untuk membedah kinerja keuangan {activeEntity === 'JUARA_GADAI' ? 'Juara Gadai Group' : 'Sahabat Gadai'}.
          </p>
        </div>

        {/* Local Page Filters */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* Area Selector */}
          <select
            value={selectedArea}
            onChange={(e) => {
              setSelectedArea(e.target.value);
              setSelectedBranch('');
            }}
            style={{ 
              padding: '8px 12px', 
              borderRadius: 'var(--radius-md)', 
              border: '1px solid var(--border-color)', 
              background: 'var(--bg-card)', 
              color: 'var(--text-primary)', 
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="">Semua Area</option>
            <option value="Depok">Depok</option>
            <option value="Bogor">Bogor</option>
            <option value="Bekasi">Bekasi</option>
          </select>

          {/* Branch Selector */}
          <select
            value={selectedBranch}
            onChange={(e) => {
              setSelectedBranch(e.target.value);
              if (e.target.value) {
                const bArea = branchToArea[e.target.value] || '';
                setSelectedArea(bArea);
              }
            }}
            style={{ 
              padding: '8px 12px', 
              borderRadius: 'var(--radius-md)', 
              border: '1px solid var(--border-color)', 
              background: 'var(--bg-card)', 
              color: 'var(--text-primary)', 
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="">Semua Cabang</option>
            {selectedArea ? (
              branchesByArea[selectedArea]?.map(b => (
                <option key={b.code} value={b.code}>{b.code} — {b.name}</option>
              ))
            ) : (
              branches.map(b => (
                <option key={b.code} value={b.code}>{b.code} — {b.name}</option>
              ))
            )}
          </select>

          {/* Month Selector */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{ 
              padding: '8px 12px', 
              borderRadius: 'var(--radius-md)', 
              border: '1px solid var(--border-color)', 
              background: 'var(--bg-card)', 
              color: 'var(--text-primary)', 
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="">Semua Bulan ({MONTHS.length})</option>
            {MONTHS.map(m => (
              <option key={m.month} value={m.month}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div 
          style={{ 
            padding: '60px 40px', 
            textAlign: 'center', 
            background: 'var(--bg-card)', 
            border: '1px solid var(--border-color)', 
            borderRadius: 'var(--radius-lg)' 
          }}
        >
          <div className="spinner" style={{ margin: '0 auto 20px', width: '44px', height: '44px', borderRadius: '50%', border: '3px solid var(--border-light)', borderTopColor: accentColor, animation: 'spin 1s linear infinite' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>Mengekstrak Jurnal Buku Besar...</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Sedang memproses seluruh tabel transaksi (~154.000 entri) untuk komputasi analitik.</p>
        </div>
      )}

      {error && (
        <div style={{ padding: '24px', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--red)', borderRadius: 'var(--radius-lg)', color: 'var(--red-light)', marginBottom: '24px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {!isLoading && !error && (
        <>
          {/* Main Navigation Tabs */}
          <div 
            style={{ 
              display: 'flex', 
              gap: '4px', 
              marginBottom: '24px', 
              borderBottom: '1px solid var(--border-color)',
              flexWrap: 'wrap'
            }}
          >
            <button className={`analytics-tab-btn ${activeTab === 'pivot' ? 'active' : ''}`} onClick={() => setActiveTab('pivot')}>
              🎛️ Dynamic Pivot Grid
            </button>
            <button className={`analytics-tab-btn ${activeTab === 'description' ? 'active' : ''}`} onClick={() => setActiveTab('description')}>
              🔎 Description Explorer
            </button>
            <button className={`analytics-tab-btn ${activeTab === 'ratios' ? 'active' : ''}`} onClick={() => setActiveTab('ratios')}>
              ⚖️ Rasio & Efisiensi
            </button>
          </div>

          {/* TAB 1: DYNAMIC PIVOT GRID */}
          {activeTab === 'pivot' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Configuration panel */}
              <div 
                style={{ 
                  background: 'rgba(255,255,255,0.01)', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: 'var(--radius-lg)', 
                  padding: '20px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '20px',
                  alignItems: 'center'
                }}
              >
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>Dimensi Baris Utama (Primary Group):</label>
                  <select
                    value={primaryDimension}
                    onChange={(e) => {
                      setPrimaryDimension(e.target.value as any);
                      setExpandedGroups(new Set());
                    }}
                    style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', background: 'var(--bg-input)', color: 'var(--text-primary)', width: '100%', cursor: 'pointer' }}
                  >
                    <option value="area">Area Wilayah</option>
                    <option value="branch">Cabang Outlet</option>
                    <option value="account">Akun Rekening (COA)</option>
                    <option value="month">Bulan Kalender</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>Dimensi Baris Sekunder (Sub-Group):</label>
                  <select
                    value={secondaryDimension}
                    onChange={(e) => {
                      setSecondaryDimension(e.target.value as any);
                      setExpandedGroups(new Set());
                    }}
                    style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', background: 'var(--bg-input)', color: 'var(--text-primary)', width: '100%', cursor: 'pointer' }}
                  >
                    <option value="account">Akun Rekening (COA)</option>
                    <option value="branch">Cabang Outlet</option>
                    <option value="area">Area Wilayah</option>
                    <option value="month">Bulan Kalender</option>
                  </select>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setExpandedGroups(new Set())}
                    disabled={expandedGroups.size === 0}
                  >
                    Collapse Semua Baris
                  </button>
                </div>
              </div>

              {/* Data Table */}
              <div 
                className="data-table-card" 
                style={{ 
                  overflowX: 'auto',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)'
                }}
              >
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '12px 16px' }}>Dimensi Matrix Keuangan</th>
                      <th style={{ textAlign: 'right', padding: '12px 16px' }}>Jumlah Transaksi</th>
                      <th style={{ textAlign: 'right', padding: '12px 16px' }}>Nilai Rata-rata</th>
                      <th style={{ textAlign: 'right', padding: '12px 16px' }}>Net Nominal Aktual</th>
                      <th style={{ textAlign: 'center', padding: '12px 16px', width: '120px' }}>Eksplorasi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pivotData.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                          Tidak ada transaksi GL yang cocok dengan kriteria filter saat ini.
                        </td>
                      </tr>
                    ) : (
                      pivotData.map((group) => {
                        const isExpanded = expandedGroups.has(group.key);
                        const allGroupRows = group.subgroups.flatMap(sg => sg.rows);

                        return (
                          <React.Fragment key={group.key}>
                            {/* Primary Group Row */}
                            <tr 
                              style={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                borderBottom: '1px solid var(--border-color)',
                                cursor: 'pointer'
                              }}
                              onClick={() => toggleGroup(group.key)}
                            >
                              <td style={{ fontWeight: '700', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: accentColor, transition: 'transform 0.2s', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                {group.label}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: '700', padding: '14px 16px', color: 'var(--text-muted)' }}>
                                {group.count.toLocaleString('id-ID')}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: '700', padding: '14px 16px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                                {formatRupiah(group.actual / group.count)}
                              </td>
                              <td 
                                style={{ 
                                  textAlign: 'right', 
                                  fontWeight: '800', 
                                  padding: '14px 16px', 
                                  fontFamily: 'monospace',
                                  color: group.actual >= 0 ? 'var(--emerald-light)' : 'var(--red-light)' 
                                }}
                              >
                                {formatRupiahFull(group.actual)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '14px 16px' }} onClick={e => e.stopPropagation()}>
                                <button 
                                  className="btn btn-secondary" 
                                  style={{ padding: '4px 10px', fontSize: '11px', height: '24px' }}
                                  onClick={() => openDrilldown(`Grup: ${group.label}`, allGroupRows)}
                                >
                                  🔍 Drill
                                </button>
                              </td>
                            </tr>

                            {/* Nested Secondary Groups */}
                            {isExpanded && group.subgroups.map((subg) => (
                              <tr 
                                key={`${group.key}|${subg.key}`} 
                                style={{ 
                                  borderBottom: '1px dashed var(--border-color)',
                                  background: 'transparent'
                                }}
                              >
                                <td style={{ padding: '10px 16px 10px 36px', color: 'var(--text-secondary)' }}>
                                  └─ {subg.label}
                                </td>
                                <td style={{ textAlign: 'right', padding: '10px 16px', color: 'var(--text-muted)', fontSize: '12px' }}>
                                  {subg.count.toLocaleString('id-ID')}
                                </td>
                                <td style={{ textAlign: 'right', padding: '10px 16px', fontFamily: 'monospace', color: 'var(--text-secondary)', fontSize: '12.5px' }}>
                                  {formatRupiah(subg.actual / subg.count)}
                                </td>
                                <td 
                                  style={{ 
                                    textAlign: 'right', 
                                    padding: '10px 16px', 
                                    fontFamily: 'monospace', 
                                    fontWeight: '600',
                                    color: subg.actual >= 0 ? 'var(--emerald-light)' : 'var(--red-light)' 
                                  }}
                                >
                                  {formatRupiah(subg.actual)}
                                </td>
                                <td style={{ textAlign: 'center', padding: '10px 16px' }}>
                                  <button 
                                    className="btn btn-secondary" 
                                    style={{ padding: '4px 10px', fontSize: '11px', height: '24px' }}
                                    onClick={() => openDrilldown(`Detail: ${group.label} ➜ ${subg.label}`, subg.rows)}
                                  >
                                    🔍 Detail
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 2: DESCRIPTION EXPLORER */}
          {activeTab === 'description' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div 
                style={{ 
                  background: 'rgba(255,255,255,0.01)', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: 'var(--radius-lg)', 
                  padding: '24px',
                }}
              >
                <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '700' }}>
                  🔎 Eksplorasi Deskripsi Jurnal (Multi-Keyword AND Search Engine)
                </h3>
                <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                  Klik salah satu tag instan atau ketik kata kunci kombinasi dipisahkan spasi (Contoh: `"Maret listrik"` untuk mencari listrik di bulan Maret). Sistem memilah seluruh deskripsi jurnal pengeluaran Buku Besar secara presisi.
                </p>

                {/* Semantic Tag list */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                  {semanticTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => {
                        setSelectedTag(tag);
                        setCustomTagInput('');
                      }}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: '1px solid',
                        borderColor: selectedTag === tag ? accentColor : 'var(--border-light)',
                        background: selectedTag === tag ? accentGlow : 'var(--bg-card)',
                        color: selectedTag === tag ? accentLight : 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: selectedTag === tag ? 700 : 500,
                        transition: 'all 0.2s'
                      }}
                    >
                      🏷️ {tag}
                    </button>
                  ))}
                </div>

                {/* Custom tag input */}
                <div style={{ display: 'flex', gap: '10px', maxWidth: '480px', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>Mesin Pencari Jurnal:</span>
                  <input
                    type="text"
                    placeholder="Ketik multi kata kunci (e.g. 'Gaji Depok')"
                    value={customTagInput}
                    onChange={(e) => {
                      setCustomTagInput(e.target.value);
                      setSelectedTag('');
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '13px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border-light)',
                      backgroundColor: 'var(--bg-input)',
                      color: 'var(--text-primary)',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Tag analysis results */}
              {(selectedTag || customTagInput.trim()) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                    <div className="kpi-metric-card">
                      <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Total Biaya untuk Pencarian "{selectedTag || customTagInput}"</span>
                      <strong style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)' }}>
                        {formatRupiahFull(tagAnalysis.totalTagAmount)}
                      </strong>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Diambil dari seluruh baris pengeluaran yang cocok di Buku Besar.</span>
                    </div>

                    <div className="kpi-metric-card">
                      <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Proporsi terhadap Total Pengeluaran Terfilter</span>
                      <strong style={{ fontSize: '24px', fontWeight: '800', color: accentColor }}>
                        {tagAnalysis.tagPercentage.toFixed(2)}%
                      </strong>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Kontribusi beban terhadap seluruh pengeluaran operasional.</span>
                    </div>
                  </div>

                  {/* Monthly trend list */}
                  <div 
                    className="data-table-card"
                    style={{
                      padding: '20px',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-lg)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>
                        📅 Distribusi Biaya Jurnal Bulanan
                      </h3>
                      <button 
                        className="btn btn-secondary"
                        style={{ padding: '4px 12px', fontSize: '12px', height: '28px' }}
                        onClick={() => openDrilldown(`Pencarian Jurnal: "${selectedTag || customTagInput}"`, tagAnalysis.matchedTrans || [])}
                        disabled={!tagAnalysis.matchedTrans || tagAnalysis.matchedTrans.length === 0}
                      >
                        🔍 Drill Transaksi Cocok ({tagAnalysis.matchedTrans?.length || 0})
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {tagTrendSort(tagAnalysis.tagTrend).map((t, idx) => {
                        const maxVal = Math.max(...tagAnalysis.tagTrend.map(x => x.amount), 1);
                        const pctBar = (t.amount / maxVal) * 100;
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '100px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>{t.label}</span>
                            <div style={{ flex: 1, height: '8px', background: 'var(--bg-input)', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pctBar}%`, background: accentColor, borderRadius: '4px' }} />
                            </div>
                            <span style={{ width: '130px', textAlign: 'right', fontFamily: 'monospace', fontWeight: '600', fontSize: '13px' }}>
                              {formatRupiah(t.amount)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: FINANCIAL RATIOS & EFFICIENCY */}
          {activeTab === 'ratios' && ratiosData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Rasio efisiensi keuangan dihitung secara real-time berdasarkan struktur pengeluaran dan pendapatan yang Anda filter. Digunakan untuk memetakan kesehatan finansial bisnis.
              </div>

              {/* Ratios metrics cards grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                {/* 1. Opex Ratio */}
                <div className="kpi-metric-card">
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Rasio Opex to Revenue</span>
                  <strong style={{ fontSize: '24px', fontWeight: '800', color: ratiosData.opexRatio < 65 ? 'var(--emerald-light)' : ratiosData.opexRatio < 85 ? '#f59e0b' : '#ef4444' }}>
                    {ratiosData.opexRatio.toFixed(1)}%
                  </strong>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Target: Di bawah 65% untuk efisiensi margin maksimal.</span>
                </div>

                {/* 2. Personnel propotion */}
                <div className="kpi-metric-card">
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Biaya Karyawan (Personnel) vs Opex</span>
                  <strong style={{ fontSize: '24px', fontWeight: '800', color: ratiosData.personnelToOpex < 65 ? 'var(--text-primary)' : '#f59e0b' }}>
                    {ratiosData.personnelToOpex.toFixed(1)}%
                  </strong>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Menggambarkan proporsi biaya pegawai terhadap beban operasional.</span>
                </div>

                {/* 3. Efficiency index */}
                <div className="kpi-metric-card">
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Indeks Efisiensi (Revenue/Opex)</span>
                  <strong style={{ fontSize: '24px', fontWeight: '800', color: ratiosData.efficiencyIndex > 1.5 ? 'var(--emerald-light)' : '#f59e0b', fontFamily: 'monospace' }}>
                    {ratiosData.efficiencyIndex.toFixed(2)}x
                  </strong>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Setiap Rp 1 opex menghasilkan Rp {ratiosData.efficiencyIndex.toFixed(2)} pendapatan.</span>
                </div>

                {/* 4. Health Score */}
                <div className="kpi-metric-card" style={{ borderColor: ratiosData.healthScore >= 70 ? '#10b981' : '#f59e0b' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Nilai Kesehatan Finansial (Health Score)</span>
                  <strong style={{ fontSize: '24px', fontWeight: '800', color: ratiosData.healthScore >= 80 ? 'var(--emerald-light)' : ratiosData.healthScore >= 60 ? '#f59e0b' : '#ef4444' }}>
                    {ratiosData.healthScore} / 100
                  </strong>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Rating Kinerja: <strong>{ratiosData.healthRating}</strong></span>
                </div>
              </div>

              {/* Ratios Breakdown Factors */}
              <div 
                className="data-table-card"
                style={{
                  padding: '24px',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)'
                }}
              >
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700' }}>
                  🩺 Penilaian Faktor Kesehatan Finansial (Health Audit Log)
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {ratiosData.factors.map((f, idx) => {
                    const icon = f.status === 'good' ? '🟢' : f.status === 'warning' ? '🟡' : '🔴';
                    const color = f.status === 'good' ? 'var(--emerald-light)' : f.status === 'warning' ? '#f59e0b' : '#ef4444';
                    return (
                      <div 
                        key={idx} 
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          background: 'rgba(255,255,255,0.01)', 
                          border: '1px solid var(--border-color)' 
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>{icon}</span>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{f.label}</span>
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: color }}>
                          {f.score === 0 ? 'Lolos Audit' : `${f.score} Poin`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Drilldown explorer window at the bottom */}
          {drilldownRows.length > 0 && (
            <div 
              id="drilldown-explorer"
              className="data-table-card animate-in"
              style={{
                marginTop: '32px',
                padding: '24px',
                border: `1px solid ${accentColor}`,
                boxShadow: `0 0 16px ${accentGlow}`,
                borderRadius: 'var(--radius-lg)'
              }}
            >
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '16px',
                  borderBottom: '1px solid var(--border-color)',
                  paddingBottom: '12px'
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🔎</span> Drilldown Explorer — {drilldownTitle}
                  </h3>
                  <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Menampilkan rincian dari {drilldownRows.length.toLocaleString('id-ID')} transaksi penyusun.
                  </p>
                </div>
                <button 
                  className="btn btn-secondary" 
                  style={{ height: '30px', fontSize: '12px' }}
                  onClick={() => {
                    setDrilldownRows([]);
                    setDrilldownTitle('');
                  }}
                >
                  Tutup Explorer ✕
                </button>
              </div>

              <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                <table className="data-table" style={{ width: '100%', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
                      <th>Bulan</th>
                      <th>Nama Akun (COA)</th>
                      <th>Deskripsi Jurnal</th>
                      <th>Area</th>
                      <th>Cabang</th>
                      <th style={{ textAlign: 'right' }}>Debit</th>
                      <th style={{ textAlign: 'right' }}>Kredit</th>
                      <th style={{ textAlign: 'right' }}>Net Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownRows.map((row, index) => {
                      const [mo, , acc_name, desc, branch, area, debit, credit, amount] = row;
                      return (
                        <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ whiteSpace: 'nowrap' }}>{mo}</td>
                          <td style={{ fontWeight: '600' }}>{acc_name}</td>
                          <td style={{ color: 'var(--text-accent)', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={desc}>
                            {desc}
                          </td>
                          <td>{area}</td>
                          <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{branch}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                            {debit > 0 ? formatRupiah(debit) : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                            {credit > 0 ? formatRupiah(credit) : '—'}
                          </td>
                          <td 
                            style={{ 
                              textAlign: 'right', 
                              fontFamily: 'monospace', 
                              fontWeight: '700',
                              color: amount >= 0 ? 'var(--emerald-light)' : 'var(--red-light)'
                            }}
                          >
                            {formatRupiah(amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Helper sorting tag trends chronologically
function tagTrendSort(trend: any[]): any[] {
  return [...trend].sort((a, b) => a.month.localeCompare(b.month));
}

export default AnalyticToolsPage;
